// src/services/research/useResearchEditor.js
// Custom-Hook für das Research-Tool. Verwaltet ZWEI Eintragstypen ("kinds") in
// einem gemeinsamen Modell: Fragen (Q&A) und Zitate. Beide teilen Form, Persistenz
// (ETag/Merge, serialisiert pro Eintrag), Ideen, Quellen, Tags und Querverweise.
// Querverweise können heterogen sein (Frage<->Zitat); ein Eintrag wird über seine
// global eindeutige id verlinkt (Präfix q_/z_).
//
// Mehr-Projekt-fähig: Der Hook ist an EIN Research-Projekt gebunden
// ({ id, sourceTable, filesPrefix, excludeFilePrefixes }); alle Lese-/Schreib-
// zugriffe laufen über dessen Partition bzw. Quellen. Ohne Projekt lädt er nichts.

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  getAllQuestions,
  createQuestion,
  getNextQuestionNo,
  saveQuestion as saveQuestionSvc,
  deleteQuestion as deleteQuestionSvc,
  getAllQuotes,
  createQuote,
  saveQuote as saveQuoteSvc,
  deleteQuote as deleteQuoteSvc,
  getAllChapters,
  createChapter,
  renameChapter as renameChapterSvc,
  reorderChapters as reorderChaptersSvc,
  deleteChapter as deleteChapterSvc,
  loadAllSources,
  filterSources,
  createManualSource,
} from "../azureResearchService";
import { loadResearchFiles } from "../azureResearchFilesService";

const AUTOSAVE_MS = 30000;

// Eintragstyp aus der id ableiten (Präfix z_ = Zitat, sonst Frage).
const kindOfId = (id) => (String(id).startsWith("z_") ? "quote" : "question");
const normalizeKind = (kind) => (kind === "quote" ? "quote" : "question");

// Vergleichs-Snapshot eines Eintrags (für die Änderungs-Erkennung im Changelog).
const snapshotOf = (e) => ({
  question: e.question || "",
  answerHtml: e.answerHtml || "",
  tags: (e.tags || []).join(""),
  ideas: JSON.stringify(e.ideas || []),
  footnotes: JSON.stringify(e.footnotes || []),
  // rk + note, damit auch eine geänderte Ergänzung als Änderung erkannt wird.
  linkedSources: JSON.stringify(
    (e.linkedSources || []).map((r) => `${r.rk}:${r.note || ""}`).sort()
  ),
  crossrefs: JSON.stringify((e.crossrefs || []).map((r) => `${r.kind}:${r.id}`).sort()),
  dataTable: JSON.stringify(e.dataTable || null),
  chart: JSON.stringify(e.chart || null),
  sqlQuery: e.sqlQuery || "",
  chapterId: e.chapterId || "",
  subchapterId: e.subchapterId || "",
  parentId: e.parentId || "",
});

// Liefert die geänderten Feld-Labels gegenüber dem letzten gespeicherten Snapshot.
// Labels sind kind-spezifisch (Frage/Antwort vs. Kontext/Zitat).
const diffFields = (prev, e) => {
  const cur = snapshotOf(e);
  const isQuote = e.kind === "quote";
  const out = [];
  if (!prev || prev.question !== cur.question) out.push(isQuote ? "Kontext" : "Frage");
  if (!prev || prev.answerHtml !== cur.answerHtml) out.push(isQuote ? "Zitat" : "Antwort");
  if (!prev || prev.tags !== cur.tags) out.push("Tags");
  if (!prev || prev.ideas !== cur.ideas) out.push("Ideen");
  if (!prev || prev.footnotes !== cur.footnotes) out.push("Fussnoten");
  if (!prev || prev.linkedSources !== cur.linkedSources) out.push("Quellen");
  if (!prev || prev.crossrefs !== cur.crossrefs) out.push("Querverweise");
  if (!prev || prev.dataTable !== cur.dataTable) out.push("Tabelle");
  if (!prev || prev.chart !== cur.chart) out.push("Diagramm");
  if (!prev || prev.sqlQuery !== cur.sqlQuery) out.push("SQL-Query");
  if (!prev || prev.chapterId !== cur.chapterId) out.push("Kapitel");
  if (!prev || prev.subchapterId !== cur.subchapterId) out.push("Unterkapitel");
  if (!prev || prev.parentId !== cur.parentId) out.push("Folgefrage");
  return out;
};

const genId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const crossKey = (r) => `${r.kind}:${r.id}`;

export function useResearchEditor(user, project) {
  // Stabile Primitive des Projekts (Objekt-Identität des project-Props ist egal).
  const projectId = project?.id || "";
  const sourceTable = project?.sourceTable || "";
  const filesPrefix = project?.filesPrefix || "";
  const excludeFilePrefixes = useMemo(
    () => project?.excludeFilePrefixes || [],
    // join als Vergleichsschlüssel, damit ein neu erzeugtes Array nicht neu lädt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(project?.excludeFilePrefixes || []).join("|")]
  );

  const [questions, setQuestions] = useState([]);
  const [quotes, setQuotes] = useState([]);
  // ALLE Kapitel-Entitäten (Kapitel + Unterkapitel, unterschieden über
  // parentChapterId) in Anzeigereihenfolge; abgeleitet: topChapters/subchaptersByChapter.
  const [chapters, setChapters] = useState([]);
  const [allSources, setAllSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [dirtyTick, setDirtyTick] = useState(0);

  const dirtyRef = useRef(new Set());
  // Synchrone Spiegel beider Listen (für Schreibvorgänge im Microtask vor Re-Render).
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const quotesRef = useRef(quotes);
  quotesRef.current = quotes;
  const chaptersRef = useRef(chapters);
  chaptersRef.current = chapters;
  // id -> Snapshot des zuletzt gespeicherten Zustands (für Changelog-Diff).
  const lastSavedRef = useRef(new Map());
  // id -> Promise-Kette: serialisiert Schreibvorgänge pro Eintrag.
  const saveChainRef = useRef(new Map());
  // Zähler laufender Schreibvorgänge (für den "Speichern…"-Status).
  const savingCountRef = useRef(0);

  const createdBy = user?.userDetails || user?.email || user?.name || "";

  // State + synchroner Spiegel der passenden Liste in einem Schritt aktualisieren.
  const updateList = useCallback((kind, updater) => {
    if (kind === "quote") {
      const next = typeof updater === "function" ? updater(quotesRef.current) : updater;
      quotesRef.current = next;
      setQuotes(next);
    } else {
      const next = typeof updater === "function" ? updater(questionsRef.current) : updater;
      questionsRef.current = next;
      setQuestions(next);
    }
  }, []);

  const listRefFor = (kind) => (kind === "quote" ? quotesRef.current : questionsRef.current);

  // --- Initial Load (je Projekt; lädt bei Projektwechsel neu) ---------------
  useEffect(() => {
    if (!projectId) return undefined; // Projekt-Metadaten noch nicht geladen
    let cancelled = false;
    // Zustand des vorherigen Projekts verwerfen (falls kein Remount stattfand).
    questionsRef.current = [];
    setQuestions([]);
    quotesRef.current = [];
    setQuotes([]);
    chaptersRef.current = [];
    setChapters([]);
    setAllSources([]);
    setEditingId(null);
    dirtyRef.current = new Set();
    lastSavedRef.current = new Map();
    saveChainRef.current = new Map();
    (async () => {
      setLoading(true);
      try {
        // Fragen + Zitate + Quellen (Tabellen-Bookmarks + Blob-Dateien) parallel laden.
        const [qs, zs, chs, tableSrcs, fileSrcs] = await Promise.all([
          getAllQuestions(projectId),
          getAllQuotes(projectId),
          getAllChapters(projectId),
          loadAllSources(sourceTable, projectId),
          loadResearchFiles(filesPrefix, excludeFilePrefixes),
        ]);
        if (cancelled) return;
        questionsRef.current = qs;
        setQuestions(qs);
        quotesRef.current = zs;
        setQuotes(zs);
        chaptersRef.current = chs;
        setChapters(chs);
        setAllSources([...tableSrcs, ...fileSrcs]);
        [...qs, ...zs].forEach((e) => lastSavedRef.current.set(e.id, snapshotOf(e)));
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, sourceTable, filesPrefix, excludeFilePrefixes]);

  // Quellen (Tabelle + Blob-Dateien) neu laden – z.B. nach extern hinzugefügten Daten.
  const refreshSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const [tableSrcs, fileSrcs] = await Promise.all([
        loadAllSources(sourceTable, projectId),
        loadResearchFiles(filesPrefix, excludeFilePrefixes),
      ]);
      setAllSources([...tableSrcs, ...fileSrcs]);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSourcesLoading(false);
    }
  }, [sourceTable, projectId, filesPrefix, excludeFilePrefixes]);

  // --- Persistenz -----------------------------------------------------------
  // Schreibt IMMER den aktuellsten Stand (liest aus dem synchronen Spiegel, nicht aus
  // einer veralteten Closure). Das Merge-Ergebnis aus dem Service (bei ETag-Konflikt
  // evtl. fremde Ideen/Quellen/Tags/Querverweise) wird additiv eingearbeitet.
  const flushSave = useCallback(
    async (id) => {
      const kind = kindOfId(id);
      const e = listRefFor(kind).find((x) => x.id === id);
      if (!e) {
        dirtyRef.current.delete(id);
        return;
      }
      // Changelog: was hat sich seit dem letzten Speichern geändert?
      const fields = diffFields(lastSavedRef.current.get(id), e);
      let toSave = e;
      if (fields.length > 0) {
        const entry = { by: createdBy || "unbekannt", at: new Date().toISOString(), fields };
        toSave = { ...e, changeLog: [entry, ...(e.changeLog || [])].slice(0, 3) };
      }
      const saveSvc = kind === "quote" ? saveQuoteSvc : saveQuestionSvc;
      const saved = await saveSvc(projectId, toSave);
      setLastSavedAt(new Date());
      lastSavedRef.current.set(id, snapshotOf(saved));
      // Merge-Ergebnis übernehmen: additive Felder mit dem lokalen Stand vereinen
      // (lokal gewinnt bei gleicher id/rk, damit zwischenzeitliche Neuzugänge bleiben).
      updateList(kind, (prev) =>
        prev.map((x) => {
          if (x.id !== id) return x;
          const ideaById = new Map();
          (saved.ideas || []).forEach((i) => ideaById.set(i.id, i));
          (x.ideas || []).forEach((i) => ideaById.set(i.id, i));
          const footnoteById = new Map();
          (saved.footnotes || []).forEach((i) => footnoteById.set(i.id, i));
          (x.footnotes || []).forEach((i) => footnoteById.set(i.id, i));
          const srcByRk = new Map();
          (saved.linkedSources || []).forEach((r) => srcByRk.set(r.rk, r));
          (x.linkedSources || []).forEach((r) => srcByRk.set(r.rk, r));
          const tagByKey = new Map();
          [...(saved.tags || []), ...(x.tags || [])].forEach((t) => {
            const k = String(t).toLowerCase();
            if (!tagByKey.has(k)) tagByKey.set(k, t);
          });
          const crossByKey = new Map();
          (saved.crossrefs || []).forEach((r) => crossByKey.set(crossKey(r), r));
          (x.crossrefs || []).forEach((r) => crossByKey.set(crossKey(r), r));
          return {
            ...x,
            ideas: Array.from(ideaById.values()),
            footnotes: Array.from(footnoteById.values()),
            linkedSources: Array.from(srcByRk.values()),
            tags: Array.from(tagByKey.values()),
            crossrefs: Array.from(crossByKey.values()),
            changeLog: saved.changeLog,
            updatedAt: saved.updatedAt,
            etag: saved.etag,
          };
        })
      );
      // Wurde während des Speicherns weiter editiert? Dann dirty halten.
      const after = listRefFor(kind).find((x) => x.id === id);
      if (after && diffFields(lastSavedRef.current.get(id), after).length > 0) {
        dirtyRef.current.add(id);
        setDirtyTick((n) => n + 1);
      } else {
        dirtyRef.current.delete(id);
      }
    },
    [createdBy, updateList, projectId]
  );

  // Speichern serialisiert pro Eintrag (Promise-Kette). Der zurückgegebene Promise
  // lehnt nie ab (Fehler via setError), damit Aufrufer ihn gefahrlos ignorieren können.
  const persist = useCallback(
    (idOrEntity) => {
      const id = typeof idOrEntity === "string" ? idOrEntity : idOrEntity?.id;
      if (!id) return Promise.resolve();
      const prev = saveChainRef.current.get(id) || Promise.resolve();
      const run = prev.then(async () => {
        savingCountRef.current += 1;
        setIsSaving(true);
        try {
          await flushSave(id);
        } catch (e) {
          dirtyRef.current.add(id);
          setDirtyTick((n) => n + 1);
          setError(e?.message || String(e));
        } finally {
          savingCountRef.current -= 1;
          if (savingCountRef.current === 0) setIsSaving(false);
        }
      });
      saveChainRef.current.set(id, run);
      return run;
    },
    [flushSave]
  );

  const saveDirty = useCallback(async () => {
    const ids = Array.from(dirtyRef.current);
    if (ids.length === 0) return;
    await Promise.all(ids.map((id) => persist(id)));
  }, [persist]);

  // Auto-Save: Timer wird bei jeder Änderung (dirtyTick) neu aufgezogen.
  useEffect(() => {
    if (dirtyRef.current.size === 0) return undefined;
    const t = setTimeout(() => {
      saveDirty();
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [dirtyTick, saveDirty]);

  const markDirty = useCallback((id) => {
    dirtyRef.current.add(id);
    setDirtyTick((n) => n + 1);
  }, []);

  // --- Eintrags-Operationen (kind-aware) ------------------------------------
  const addEntity = useCallback(
    async (kind) => {
      const k = normalizeKind(kind);
      if (!projectId) return null;
      try {
        // Fragen erhalten eine feste, unveränderliche Nummer (Anzeige "ID001");
        // Zitate werden nicht nummeriert.
        const created =
          k === "quote"
            ? await createQuote(projectId, { createdBy })
            : await createQuestion(projectId, {
                createdBy,
                questionNo: String((await getNextQuestionNo(projectId)) || ""),
              });
        lastSavedRef.current.set(created.id, snapshotOf(created));
        updateList(k, (prev) => [...prev, created]);
        setEditingId(created.id);
        return created;
      } catch (e) {
        setError(e?.message || String(e));
        return null;
      }
    },
    [createdBy, updateList, projectId]
  );

  // Direkt eine Folgefrage (Folgefrage) unter `parentId` anlegen und zum Bearbeiten
  // öffnen. Spart den Umweg ueber "neue Frage + Mutterfrage zuweisen".
  // Kapitel/Unterkapitel bleiben leer: Folgefragen erben sie von der Wurzel
  // (gleiche Semantik wie moveQuestion). Einsortiert ans Ende der Geschwister.
  const addSubQuestion = useCallback(
    async (parentId) => {
      if (!projectId || !parentId) return null;
      try {
        const maxOrder = questionsRef.current
          .filter((q) => (q.parentId || "") === parentId)
          .reduce((max, q) => Math.max(max, Number(q.sortOrder) || 0), -1);
        const created = await createQuestion(projectId, {
          createdBy,
          parentId,
          sortOrder: String(maxOrder + 1),
          questionNo: String((await getNextQuestionNo(projectId)) || ""),
        });
        lastSavedRef.current.set(created.id, snapshotOf(created));
        updateList("question", (prev) => [...prev, created]);
        setEditingId(created.id);
        return created;
      } catch (e) {
        setError(e?.message || String(e));
        return null;
      }
    },
    [createdBy, updateList, projectId]
  );

  // Plain-Text-Slot (Frage: Fragetext; Zitat: Kontext-Notiz).
  const setPlainField = useCallback(
    (id, value) => {
      const kind = kindOfId(id);
      updateList(kind, (prev) => prev.map((x) => (x.id === id ? { ...x, question: value } : x)));
      markDirty(id);
    },
    [markDirty, updateList]
  );

  // Rich-Text-Slot (Frage: Antwort; Zitat: Zitattext).
  const setRichField = useCallback(
    (id, html) => {
      const kind = kindOfId(id);
      updateList(kind, (prev) => prev.map((x) => (x.id === id ? { ...x, answerHtml: html } : x)));
      markDirty(id);
    },
    [markDirty, updateList]
  );

  const saveEntityNow = useCallback(
    (id) => {
      if (dirtyRef.current.has(id)) return persist(id);
      return Promise.resolve();
    },
    [persist]
  );

  const removeEntity = useCallback(
    async (id) => {
      const kind = kindOfId(id);
      try {
        if (kind === "quote") await deleteQuoteSvc(projectId, id);
        else await deleteQuestionSvc(projectId, id);
        dirtyRef.current.delete(id);
        saveChainRef.current.delete(id);
        lastSavedRef.current.delete(id);
        updateList(kind, (prev) => prev.filter((x) => x.id !== id));
        setEditingId((cur) => (cur === id ? null : cur));
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [updateList, projectId]
  );

  // --- Helfer: Eintrag lokal ändern + sofort persistieren -------------------
  const mutateAndPersist = useCallback(
    (id, mutate) => {
      const kind = kindOfId(id);
      const e = listRefFor(kind).find((x) => x.id === id);
      if (!e) return Promise.resolve();
      const next = mutate(e);
      if (!next || next === e) return Promise.resolve(); // keine Änderung
      updateList(kind, (prev) => prev.map((x) => (x.id === id ? next : x)));
      return persist(id);
    },
    [persist, updateList]
  );

  // --- Kapitel + Unterkapitel (Story: Strukturierung, ADR 0038) -------------
  // Kapitelzuordnung einer Frage (sofort persistiert). "" = nicht zugeordnet.
  // Ein Kapitelwechsel setzt das Unterkapitel zurück (gehört zum alten Kapitel).
  const setQuestionChapter = useCallback(
    (id, chapterId) =>
      mutateAndPersist(id, (e) =>
        e.chapterId === (chapterId || "")
          ? e
          : { ...e, chapterId: chapterId || "", subchapterId: "" }
      ),
    [mutateAndPersist]
  );

  // Unterkapitelzuordnung einer Frage (innerhalb ihres Kapitels). "" = direkt im Kapitel.
  const setQuestionSubchapter = useCallback(
    (id, subchapterId) =>
      mutateAndPersist(id, (e) =>
        e.subchapterId === (subchapterId || "") ? e : { ...e, subchapterId: subchapterId || "" }
      ),
    [mutateAndPersist]
  );

  const addChapter = useCallback(
    async (name) => {
      const n = String(name || "").trim();
      if (!projectId || !n) return null;
      // Duplikate (case-insensitiv) vermeiden – bestehendes Kapitel zurückgeben.
      const tops = chaptersRef.current.filter((c) => !c.parentChapterId);
      const existing = tops.find((c) => c.name.toLowerCase() === n.toLowerCase());
      if (existing) return existing;
      try {
        const created = await createChapter(projectId, n, tops.length);
        setChapters((prev) => {
          const next = [...prev, created];
          chaptersRef.current = next;
          return next;
        });
        return created;
      } catch (e) {
        setError(e?.message || String(e));
        return null;
      }
    },
    [projectId]
  );

  // Unterkapitel anlegen (Duplikate je Kapitel case-insensitiv vermeiden).
  const addSubchapter = useCallback(
    async (chapterId, name) => {
      const n = String(name || "").trim();
      if (!projectId || !chapterId || !n) return null;
      const siblings = chaptersRef.current.filter((c) => c.parentChapterId === chapterId);
      const existing = siblings.find((c) => c.name.toLowerCase() === n.toLowerCase());
      if (existing) return existing;
      try {
        const created = await createChapter(projectId, n, siblings.length, chapterId);
        setChapters((prev) => {
          const next = [...prev, created];
          chaptersRef.current = next;
          return next;
        });
        return created;
      } catch (e) {
        setError(e?.message || String(e));
        return null;
      }
    },
    [projectId]
  );

  const renameChapter = useCallback(
    async (id, name) => {
      const n = String(name || "").trim();
      if (!n) return;
      setChapters((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, name: n } : c));
        chaptersRef.current = next;
        return next;
      });
      try {
        await renameChapterSvc(projectId, id, n);
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [projectId]
  );

  // Reihenfolge einer Geschwistergruppe setzen (Kapitel ODER Unterkapitel eines
  // Kapitels): nur die übergebenen ids bekommen neue sortOrder, alle anderen
  // Entitäten (z.B. Unterkapitel anderer Kapitel) bleiben unangetastet.
  const reorderChapters = useCallback(
    async (orderedIds) => {
      const orderMap = new Map((orderedIds || []).map((id, i) => [id, i]));
      setChapters((prev) => {
        const next = prev
          .map((c) => (orderMap.has(c.id) ? { ...c, sortOrder: orderMap.get(c.id) } : c))
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
        chaptersRef.current = next;
        return next;
      });
      try {
        await reorderChaptersSvc(projectId, orderedIds);
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [projectId]
  );

  // Kapitel löschen: betroffene Fragen werden "nicht zugeordnet"; die
  // Unterkapitel des Kapitels werden mitgelöscht.
  const deleteChapter = useCallback(
    async (id) => {
      try {
        const subs = chaptersRef.current.filter((c) => c.parentChapterId === id);
        await deleteChapterSvc(projectId, id);
        await Promise.all(subs.map((s) => deleteChapterSvc(projectId, s.id)));
        setChapters((prev) => {
          const next = prev.filter((c) => c.id !== id && c.parentChapterId !== id);
          chaptersRef.current = next;
          return next;
        });
        questionsRef.current
          .filter((q) => q.chapterId === id)
          .forEach((q) => setQuestionChapter(q.id, ""));
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [projectId, setQuestionChapter]
  );

  // Unterkapitel löschen: betroffene Fragen bleiben im Kapitel ("direkt im Kapitel").
  const deleteSubchapter = useCallback(
    async (id) => {
      try {
        await deleteChapterSvc(projectId, id);
        setChapters((prev) => {
          const next = prev.filter((c) => c.id !== id);
          chaptersRef.current = next;
          return next;
        });
        questionsRef.current
          .filter((q) => q.subchapterId === id)
          .forEach((q) => setQuestionSubchapter(q.id, ""));
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [projectId, setQuestionSubchapter]
  );

  // --- Folgefragen (Hierarchie) + Drag&Drop-Sortierung ----------------------
  // Verschiebt eine Frage (mit ihrem gesamten Teilbaum, da Kinder per parentId
  // verweisen) an eine neue Position: neuer Parent (tp), neues Kapitel/Unterkapitel
  // (nur wenn oberste Ebene) und Einfüge-Position vor beforeId (sonst ans Ende
  // der Geschwister).
  const moveQuestion = useCallback(
    (dragId, targetParentId, targetChapterId, targetSubchapterId, beforeId) => {
      const list = questionsRef.current;
      const drag = list.find((q) => q.id === dragId);
      if (!drag) return;
      const tp = targetParentId || "";
      // Zyklus-Schutz: Ziel-Parent darf nicht dragId selbst oder ein Nachfahre sein.
      if (tp) {
        const descendants = new Set();
        const collect = (pid) =>
          list.forEach((q) => {
            if ((q.parentId || "") === pid) {
              descendants.add(q.id);
              collect(q.id);
            }
          });
        collect(dragId);
        if (tp === dragId || descendants.has(tp)) return;
      }
      // Folgefragen erben Kapitel/Unterkapitel der Wurzel -> eigene Werte nur oberste Ebene.
      const newChapterId = tp ? "" : targetChapterId || "";
      const newSubchapterId = tp ? "" : targetSubchapterId || "";
      const siblings = list
        .filter((q) => (q.parentId || "") === tp && q.id !== dragId)
        .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
      let insertAt = siblings.length;
      if (beforeId) {
        const idx = siblings.findIndex((q) => q.id === beforeId);
        if (idx >= 0) insertAt = idx;
      }
      const orderedIds = [
        ...siblings.slice(0, insertAt).map((q) => q.id),
        dragId,
        ...siblings.slice(insertAt).map((q) => q.id),
      ];
      const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
      const changed = [];
      const next = list.map((q) => {
        if (q.id === dragId) {
          changed.push(q.id);
          return {
            ...q,
            parentId: tp,
            chapterId: newChapterId,
            subchapterId: newSubchapterId,
            sortOrder: String(orderMap.get(dragId)),
          };
        }
        if (orderMap.has(q.id) && String(orderMap.get(q.id)) !== String(q.sortOrder)) {
          changed.push(q.id);
          return { ...q, sortOrder: String(orderMap.get(q.id)) };
        }
        return q;
      });
      updateList("question", next);
      changed.forEach((id) => persist(id));
    },
    [persist, updateList]
  );

  // Frage als Folgefrage einer anderen zuordnen bzw. wieder lösen ("" = oberste
  // Ebene). Beim Lösen erbt sie Kapitel + Unterkapitel des bisherigen Wurzel-Vorfahren.
  const setQuestionParent = useCallback(
    (id, parentId) => {
      if (parentId) {
        moveQuestion(id, parentId, "", "", null);
        return;
      }
      const list = questionsRef.current;
      let cur = list.find((x) => x.id === id);
      const seen = new Set();
      while (cur && cur.parentId && !seen.has(cur.parentId)) {
        seen.add(cur.parentId);
        cur = list.find((x) => x.id === cur.parentId);
      }
      moveQuestion(id, "", cur?.chapterId || "", cur?.subchapterId || "", null);
    },
    [moveQuestion]
  );

  // --- Quellen-Verknüpfung (sofort persistiert) -----------------------------
  const linkSource = useCallback(
    (id, source) =>
      mutateAndPersist(id, (e) => {
        if (!source?.rk || (e.linkedSources || []).some((r) => r.rk === source.rk)) return e;
        return {
          ...e,
          linkedSources: [
            ...(e.linkedSources || []),
            { kind: source.kind === "file" ? "file" : "table", pk: source.pk || "", rk: source.rk },
          ],
        };
      }),
    [mutateAndPersist]
  );

  const unlinkSource = useCallback(
    (id, rk) =>
      mutateAndPersist(id, (e) => ({
        ...e,
        linkedSources: (e.linkedSources || []).filter((r) => r.rk !== rk),
      })),
    [mutateAndPersist]
  );

  // Ergänzung zu einer Verknüpfung setzen/ändern (z.B. "S. 41"). Die Note lebt
  // auf der Verknüpfung des Eintrags, nicht auf der Quelle selbst.
  const setSourceNote = useCallback(
    (id, rk, note) =>
      mutateAndPersist(id, (e) => {
        const n = String(note || "").trim();
        const cur = (e.linkedSources || []).find((r) => r.rk === rk);
        if (!cur || (cur.note || "") === n) return e; // keine Änderung
        return {
          ...e,
          linkedSources: (e.linkedSources || []).map((r) =>
            r.rk === rk ? { ...r, note: n } : r
          ),
        };
      }),
    [mutateAndPersist]
  );

  // Manuell erfasste Quelle (Buch/Zeitschrift/Interview) in die Quellen-Tabelle
  // des Projekts schreiben und sofort lokal verfügbar machen.
  const addManualSource = useCallback(
    async (data) => {
      if (!sourceTable || !projectId) return null;
      try {
        const created = await createManualSource(sourceTable, projectId, {
          ...data,
          createdBy,
        });
        setAllSources((prev) => [created, ...prev]);
        return created;
      } catch (e) {
        setError(e?.message || String(e));
        return null;
      }
    },
    [sourceTable, projectId, createdBy]
  );

  // --- Tags (sofort persistiert) --------------------------------------------
  const addTag = useCallback(
    (id, tag) =>
      mutateAndPersist(id, (e) => {
        const t = String(tag || "").trim();
        if (!t) return e;
        if ((e.tags || []).some((x) => x.toLowerCase() === t.toLowerCase())) return e;
        return { ...e, tags: [...(e.tags || []), t] };
      }),
    [mutateAndPersist]
  );

  const removeTag = useCallback(
    (id, tag) =>
      mutateAndPersist(id, (e) => ({
        ...e,
        tags: (e.tags || []).filter((x) => x !== tag),
      })),
    [mutateAndPersist]
  );

  // --- Querverweise (heterogen: {kind,id}; sofort persistiert) --------------
  const addCrossref = useCallback(
    (sourceId, target) =>
      mutateAndPersist(sourceId, (e) => {
        const t = target && { kind: normalizeKind(target.kind), id: String(target.id || "") };
        if (!t || !t.id) return e;
        if (t.id === sourceId) return e; // kein Selbstverweis
        if ((e.crossrefs || []).some((r) => r.id === t.id && r.kind === t.kind)) return e;
        return { ...e, crossrefs: [...(e.crossrefs || []), t] };
      }),
    [mutateAndPersist]
  );

  const removeCrossref = useCallback(
    (sourceId, target) =>
      mutateAndPersist(sourceId, (e) => ({
        ...e,
        crossrefs: (e.crossrefs || []).filter(
          (r) => !(r.id === target.id && r.kind === normalizeKind(target.kind))
        ),
      })),
    [mutateAndPersist]
  );

  // --- Ideen (einzeln, sofort persistiert) ----------------------------------
  const addIdea = useCallback(
    (id, text) =>
      mutateAndPersist(id, (e) => {
        const t = String(text || "").trim();
        if (!t) return e;
        const idea = {
          id: genId("idea"),
          text: t,
          createdBy: createdBy || "unbekannt",
          createdAt: new Date().toISOString(),
        };
        return { ...e, ideas: [...(e.ideas || []), idea] };
      }),
    [mutateAndPersist, createdBy]
  );

  const editIdea = useCallback(
    (id, ideaId, text) =>
      mutateAndPersist(id, (e) => {
        const t = String(text || "").trim();
        if (!t) return e; // leere Idee nicht speichern
        const ideas = (e.ideas || []).map((i) => (i.id === ideaId ? { ...i, text: t } : i));
        return { ...e, ideas };
      }),
    [mutateAndPersist]
  );

  const removeIdea = useCallback(
    (id, ideaId) =>
      mutateAndPersist(id, (e) => ({
        ...e,
        ideas: (e.ideas || []).filter((i) => i.id !== ideaId),
      })),
    [mutateAndPersist]
  );

  // --- Fussnoten (gleiches Prinzip wie Ideen; sofort persistiert) ------------
  const addFootnote = useCallback(
    (id, text) =>
      mutateAndPersist(id, (e) => {
        const t = String(text || "").trim();
        if (!t) return e;
        const footnote = {
          id: genId("fn"),
          text: t,
          createdBy: createdBy || "unbekannt",
          createdAt: new Date().toISOString(),
        };
        return { ...e, footnotes: [...(e.footnotes || []), footnote] };
      }),
    [mutateAndPersist, createdBy]
  );

  const editFootnote = useCallback(
    (id, footnoteId, text) =>
      mutateAndPersist(id, (e) => {
        const t = String(text || "").trim();
        if (!t) return e; // leere Fussnote nicht speichern
        const footnotes = (e.footnotes || []).map((i) =>
          i.id === footnoteId ? { ...i, text: t } : i
        );
        return { ...e, footnotes };
      }),
    [mutateAndPersist]
  );

  const removeFootnote = useCallback(
    (id, footnoteId) =>
      mutateAndPersist(id, (e) => ({
        ...e,
        footnotes: (e.footnotes || []).filter((i) => i.id !== footnoteId),
      })),
    [mutateAndPersist]
  );

  // --- Daten-Tabelle + Diagramm --------------------------------------------
  // Sofort persistiert (Upload, Zeile/Spalte ±, Tabelle entfernen, Diagramm).
  const setDataTable = useCallback(
    (id, table) => mutateAndPersist(id, (e) => ({ ...e, dataTable: table || null })),
    [mutateAndPersist]
  );

  // Nur lokal + dirty (Zell-/Spaltennamen-Tippen) — Persistenz via Blur/Auto-Save,
  // damit nicht jeder Tastendruck einen Schreibvorgang auslöst.
  const setDataTableLocal = useCallback(
    (id, table) => {
      const kind = kindOfId(id);
      updateList(kind, (prev) => prev.map((x) => (x.id === id ? { ...x, dataTable: table } : x)));
      markDirty(id);
    },
    [markDirty, updateList]
  );

  const clearDataTable = useCallback(
    (id) => mutateAndPersist(id, (e) => ({ ...e, dataTable: null, chart: null })),
    [mutateAndPersist]
  );

  const setChart = useCallback(
    (id, config) => mutateAndPersist(id, (e) => ({ ...e, chart: config || null })),
    [mutateAndPersist]
  );

  // Nur lokal + dirty (z.B. Live-Farbwahl im Color-Picker) — Persistenz via Blur/Auto-Save.
  const setChartLocal = useCallback(
    (id, config) => {
      const kind = kindOfId(id);
      updateList(kind, (prev) => prev.map((x) => (x.id === id ? { ...x, chart: config } : x)));
      markDirty(id);
    },
    [markDirty, updateList]
  );

  // SQL-Query (Datenherkunft) — Tippen nur lokal + dirty, Persistenz via Blur/Auto-Save.
  const setSqlQueryLocal = useCallback(
    (id, text) => {
      const kind = kindOfId(id);
      updateList(kind, (prev) => prev.map((x) => (x.id === id ? { ...x, sqlQuery: text } : x)));
      markDirty(id);
    },
    [markDirty, updateList]
  );

  // --- Abgeleitetes ---------------------------------------------------------
  // Kapitel (oberste Ebene) und Unterkapitel je Kapitel, jeweils in Reihenfolge.
  const topChapters = useMemo(
    () => chapters.filter((c) => !c.parentChapterId),
    [chapters]
  );
  const subchaptersByChapter = useMemo(() => {
    const m = new Map();
    chapters.forEach((c) => {
      if (!c.parentChapterId) return;
      if (!m.has(c.parentChapterId)) m.set(c.parentChapterId, []);
      m.get(c.parentChapterId).push(c);
    });
    return m;
  }, [chapters]);

  const sourcesById = useMemo(() => {
    const m = new Map();
    allSources.forEach((s) => m.set(s.rk, s));
    return m;
  }, [allSources]);

  const searchResults = useMemo(
    () => filterSources(allSources, searchTerm),
    [allSources, searchTerm]
  );

  // Alle Einträge (Fragen + Zitate) – für Querverweis-Suche und -Auflösung.
  const allEntities = useMemo(() => [...questions, ...quotes], [questions, quotes]);

  const entityById = useMemo(() => {
    const m = new Map();
    allEntities.forEach((e) => m.set(e.id, e));
    return m;
  }, [allEntities]);

  const resolveEntity = useCallback((id) => entityById.get(id) || null, [entityById]);

  // Alle bereits verwendeten Tags (für Vorschläge), alphabetisch – über beide kinds.
  const allTags = useMemo(() => {
    const set = new Set();
    allEntities.forEach((e) => (e.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEntities]);

  // Verknüpfungen zu Quellen-Objekten auflösen; die Ergänzungs-Note der
  // Verknüpfung wird ans aufgelöste Objekt angehängt (s.note).
  const resolveSources = useCallback(
    (refs) =>
      (refs || []).map((r) => {
        const found = sourcesById.get(r.rk);
        const base = found || {
          rk: r.rk,
          pk: r.pk,
          title: "Quelle nicht gefunden",
          content: "",
          url: "",
          favicon: "",
          thumbnailData: "",
          blobUrl: "",
          tags: "",
          thema: "",
          missing: true,
        };
        return { ...base, note: r.note || "" };
      }),
    [sourcesById]
  );

  const isDirty = dirtyRef.current.size > 0;

  return {
    questions,
    quotes,
    // Kapitel = nur oberste Ebene (UI-kompatibel); Unterkapitel separat.
    chapters: topChapters,
    subchaptersByChapter,
    allEntities,
    allSources,
    loading,
    error,
    editingId,
    setEditingId,
    searchTerm,
    setSearchTerm,
    searchResults,
    sourcesLoading,
    refreshSources,
    allTags,
    isSaving,
    isDirty,
    lastSavedAt,
    // Eintrags-Ops (id-basiert, kind aus id abgeleitet)
    addEntity,
    setPlainField,
    setRichField,
    saveEntityNow,
    saveDirty,
    removeEntity,
    linkSource,
    unlinkSource,
    setSourceNote,
    // Manuelle Quellen (Buch/Zeitschrift/Interview)
    addManualSource,
    canCreateSources: !!(sourceTable && projectId),
    addTag,
    removeTag,
    addCrossref,
    removeCrossref,
    addIdea,
    editIdea,
    removeIdea,
    addFootnote,
    editFootnote,
    removeFootnote,
    setDataTable,
    setDataTableLocal,
    clearDataTable,
    setChart,
    setChartLocal,
    setSqlQueryLocal,
    resolveSources,
    resolveEntity,
    // Kapitel + Unterkapitel (Story: Strukturierung, ADR 0038)
    setQuestionChapter,
    setQuestionSubchapter,
    addChapter,
    addSubchapter,
    renameChapter,
    reorderChapters,
    deleteChapter,
    deleteSubchapter,
    // Folgefragen + DnD-Sortierung
    addSubQuestion,
    moveQuestion,
    setQuestionParent,
  };
}
